<template>
  <div class="app-container complaint-feedback">
    <!-- 顶部五个统计卡：待处理 / 处理中 / 待客户确认 / 已完成 / 已归档 -->
    <el-card class="box-card">
      <div class="stat-row">
        <div class="stat-item" v-for="item in COMPLAINT_STATE_LIST" :key="item.value" @click="handleStatClick(item.value)">
          <span class="stat-icon" :class="item.tagClass">
            <i :class="statIcon(item.value)"></i>
          </span>
          <div class="stat-body">
            <div class="stat-label">{{item.label}}</div>
            <div class="stat-value">{{statistics[item.value] || 0}}</div>
          </div>
        </div>
      </div>
      <div class="filter-container mt10">
        <el-form :model="query" ref="queryForm" :inline="true">
          <el-form-item label="反馈编号：">
            <el-input v-model="query.complaintNo" placeholder="请输入反馈编号" clearable size="small" />
          </el-form-item>
          <el-form-item label="客户姓名/电话：">
            <el-input v-model="query.nameOrPhone" placeholder="姓名/电话" clearable size="small" />
          </el-form-item>
          <el-form-item label="反馈类型：">
            <el-select v-model="query.typeId" placeholder="请选择" clearable size="small">
              <el-option v-for="item in typeOptions" :key="item.id" :label="item.typeName" :value="item.id"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="处理状态：">
            <el-select v-model="query.state" placeholder="请选择" clearable size="small">
              <el-option v-for="item in COMPLAINT_STATE_LIST" :key="item.value" :label="item.label" :value="item.value"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="提交时间：">
            <el-date-picker v-model="dateRange" size="small" type="daterange" value-format="yyyy-MM-dd" start-placeholder="选择开始日期" end-placeholder="选择结束日期" />
          </el-form-item>
          <!-- 方案要求可按回复时间筛。与「提交时间」是两组独立条件，可同时用；
               从没回复过的单子 first_reply_time 为空，选了这组条件就不会出现在结果里 -->
          <el-form-item label="回复时间：">
            <el-date-picker v-model="replyDateRange" size="small" type="daterange" value-format="yyyy-MM-dd" start-placeholder="选择开始日期" end-placeholder="选择结束日期" />
          </el-form-item>
          <el-form-item label="业务老师：">
            <el-select v-model="query.businessUserName" filterable clearable size="small" placeholder="请选择" :filter-method="businessFilter" @blur="getBusinessList()">
              <el-option v-for="item in businessList" :key="item.id" :label="item.name" :value="item.name"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="技术老师：">
            <el-select v-model="query.techUserName" filterable clearable size="small" placeholder="请选择" :filter-method="technicianFilter" @blur="getTechnicianList()">
              <el-option v-for="item in technicianList" :key="item.id" :label="item.realName" :value="item.realName"></el-option>
            </el-select>
          </el-form-item>
          <!-- 需求第 8 条：按反馈人所属区域筛选。原型筛选区没画这个框，是按需求补的。
               省/市/区三级联动，复用 clientList 那套 sysAddr 树 + el-cascader 写法 -->
          <el-form-item label="所属区域：">
            <el-cascader v-model="addrCascader" :props="cascaderProps" :options="addressList" :show-all-levels="false" clearable placeholder="请选择所属区域" size="small" />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" size="mini" @click="handleQuery">查询</el-button>
            <el-button type="info" size="mini" plain @click="handleReset">重置</el-button>
            <el-button type="primary" size="mini" plain icon="el-icon-download" @click="handleExport" v-hasPermi="['complaint:feedback:export']">导出数据</el-button>
            <el-button type="primary" size="mini" plain icon="el-icon-files" @click="handleBatchExport" v-hasPermi="['complaint:feedback:export']">批量导出</el-button>
          </el-form-item>
        </el-form>
      </div>
    </el-card>

    <el-card class="box-card mt10">
      <el-table v-loading="listLoading" :data="list" @selection-change="selectionChange">
        <el-table-column type="selection" width="50" />
        <el-table-column label="反馈编号" prop="complaintNo" align="center" width="150" />
        <el-table-column label="反馈类型" align="center" width="110">
          <template slot-scope="{row}">
            <el-tag size="mini" :type="typeTagType(row.typeName)">{{row.typeName}}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="客户姓名" prop="customerName" align="center" width="100" />
        <el-table-column label="联系电话" prop="customerPhone" align="center" width="130" />
        <el-table-column label="地址" align="center" min-width="180" show-overflow-tooltip>
          <template slot-scope="{row}">{{fullAddress(row)}}</template>
        </el-table-column>
        <!-- 方案第 16 条要求列表展示身份和所属大区，原型没画所以此前没做。
             identityName / regionName 都是提交时的快照值，不随客户档案后续变更 -->
        <el-table-column label="身份" prop="identityName" align="center" width="100">
          <template slot-scope="{row}">{{row.identityName || '-'}}</template>
        </el-table-column>
        <el-table-column label="所属大区" prop="regionName" align="center" width="110">
          <template slot-scope="{row}">{{row.regionName || '-'}}</template>
        </el-table-column>
        <el-table-column label="业务老师" prop="businessUserName" align="center" width="100" />
        <el-table-column label="技术老师" prop="techUserName" align="center" width="100" />
        <el-table-column label="问题摘要" align="center" min-width="160">
          <template slot-scope="{row}">
            <!-- BUG-2026-686228：列表只截 10 字，原来唯一能进详情的入口是操作列的「处理」——
                 测试和运营都认「点摘要看全文」，点了没反应就报「进不了详情」（访问日志证明接口一直 200）。
                 摘要做成可点，和操作列走同一个弹窗。 -->
            <el-link type="primary" :underline="false" @click="handleDetail(row)">{{summary(row.content)}}</el-link>
          </template>
        </el-table-column>
        <el-table-column label="提交时间" prop="createdTime" align="center" width="160" />
        <el-table-column label="首次回复时间" prop="firstReplyTime" align="center" width="160">
          <template slot-scope="{row}">{{row.firstReplyTime || '-'}}</template>
        </el-table-column>
        <el-table-column label="状态" align="center" width="110">
          <template slot-scope="{row}">
            <el-tag size="mini" :type="findStateItem(row.state).tagType" :class="findStateItem(row.state).tagClass">{{findStateItem(row.state).label}}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" align="center" width="160" fixed="right">
          <template slot-scope="{row}">
            <!-- 「详情」和「处理」是同一个弹窗：已归档的单子只剩查看，按钮文案跟着变，别让人以为归档后看不了 -->
            <el-button type="text" @click="handleDetail(row)">{{ isArchived(row) ? '详情' : '处理' }}</el-button>
            <!-- 已归档后不可再加备注 / 再归档 -->
            <el-button type="text" :disabled="isArchived(row)" @click="handleRemark(row)" v-hasPermi="['complaint:feedback:remark']">备注</el-button>
            <el-button type="text" :disabled="isArchived(row)" @click="handleArchive(row)" v-hasPermi="['complaint:feedback:archive']">归档</el-button>
          </template>
        </el-table-column>
      </el-table>
      <pagination v-show="total>0" :total="total" :pageNo.sync="query.pageNo" :pageSize.sync="query.pageSize" @pagination="doQueryList" />
    </el-card>

    <complaint-handle ref="complaintHandle" @created="refresh" />
    <complaint-remark ref="complaintRemark" @created="refresh" />
  </div>
</template>

<script>
import Pagination from '@/components/Pagination'
import customerComplaintApi from '@/api/complaint/customerComplaint'
import customerComplaintTypeApi from '@/api/complaint/customerComplaintType'
import clientListApi from '@/api/client/clientList'
import technicianInfoApi from '@/api/technical/technicianInfo'
import sysAddrApi from '@/api/sys/sysAddr'
import ComplaintHandle from './handle.vue'
import ComplaintRemark from './remark.vue'
import { COMPLAINT_STATE_LIST, STATE_ARCHIVED, findStateItem } from '../complaintState'

// 反馈类型标签配色（照原型 49.png 里初始化的 4 个类型）
const TYPE_TAG_MAP = {
  '产品售后': 'danger',
  '投诉建议': 'warning',
  '网销举报': 'success',
  '其他问题': 'info'
}

function initQuery() {
  return {
    pageNo: 1,
    pageSize: 10,
    complaintNo: undefined,
    nameOrPhone: undefined,
    typeId: undefined,
    state: undefined,
    startTime: undefined,
    endTime: undefined,
    businessUserName: undefined,
    techUserName: undefined,
    provinceCode: undefined,
    cityCode: undefined,
    areaCode: undefined
  }
}

export default {
  name: 'ComplaintFeedback',
  components: { Pagination, ComplaintHandle, ComplaintRemark },
  data() {
    return {
      COMPLAINT_STATE_LIST,
      list: [],
      total: 0,
      listLoading: false,
      query: initQuery(),
      dateRange: [],
      replyDateRange: [],
      // 省/市/区三级联动选中的 code 路径，如 ['110000','110100','110101']
      addrCascader: [],
      cascaderProps: {
        label: 'regionName',
        value: 'regionCode',
        children: 'child',
        checkStrictly: true
      },
      addressList: [],
      statistics: {},
      typeOptions: [],
      businessList: [],
      technicianList: [],
      selectItems: []
    }
  },
  created() {
    this.getTypeOptions()
    this.getBusinessList()
    this.getTechnicianList()
    this.getAddressList()
    this.refresh()
  },
  methods: {
    findStateItem,
    isArchived(row) {
      return +row.state === STATE_ARCHIVED
    },
    statIcon(state) {
      const map = {
        1: 'el-icon-time',
        2: 'el-icon-user',
        3: 'el-icon-s-custom',
        4: 'el-icon-circle-check',
        5: 'el-icon-folder-opened'
      }
      return map[state]
    },
    typeTagType(typeName) {
      return TYPE_TAG_MAP[typeName] || ''
    },
    fullAddress(row) {
      return [row.addrProvince, row.addrCity, row.addrArea, row.addrDetail].filter(v => !!v).join('')
    },
    summary(content) {
      if (!content) return ''
      return content.length > 10 ? content.substring(0, 10) + '...' : content
    },
    selectionChange(val) {
      this.selectItems = val
    },
    refresh() {
      this.getStatistics()
      return this.doQueryList({})
    },
    handleQuery() {
      this.doQueryList({ pageNo: 1 })
      this.getStatistics()
    },
    handleReset() {
      this.query = initQuery()
      this.dateRange = []
      this.replyDateRange = []
      this.addrCascader = []
      this.doQueryList({ pageNo: 1 })
      this.getStatistics()
    },
    /**
     * 点统计卡按该状态筛选
     */
    handleStatClick(state) {
      this.query.state = state
      this.doQueryList({ pageNo: 1 })
    },
    calcQuery() {
      const params = { ...this.query }
      if (this.dateRange && this.dateRange.length === 2) {
        params.startTime = this.dateRange[0] + ' 00:00:00'
        params.endTime = this.dateRange[1] + ' 23:59:59'
      } else {
        params.startTime = undefined
        params.endTime = undefined
      }
      if (this.replyDateRange && this.replyDateRange.length === 2) {
        params.replyStartTime = this.replyDateRange[0] + ' 00:00:00'
        params.replyEndTime = this.replyDateRange[1] + ' 23:59:59'
      } else {
        params.replyStartTime = undefined
        params.replyEndTime = undefined
      }
      // 所属区域：级联选中的是 code 路径，按层级拆成 provinceCode / cityCode / areaCode
      const addr = this.addrCascader || []
      params.provinceCode = addr[0] || undefined
      params.cityCode = addr[1] || undefined
      params.areaCode = addr[2] || undefined
      return params
    },
    doQueryList({ pageNo, pageSize }) {
      if (pageNo) this.query.pageNo = pageNo
      if (pageSize) this.query.pageSize = pageSize
      this.listLoading = true
      return customerComplaintApi.fetchList(this.calcQuery())
        .then(data => {
          this.list = (data.data && data.data.list) || []
          this.total = (data.data && data.data.total) || 0
        })
        .finally(() => {
          this.listLoading = false
        })
    },
    /**
     * 五个统计卡计数。后端字段名若与下面任一别名对得上都能读出来
     */
    getStatistics() {
      return customerComplaintApi.statistics(this.calcQuery()).then(data => {
        const d = (data && data.data) || {}
        this.statistics = {
          1: this.pickCount(d, ['pendingCount', 'pending', 'waitHandleCount', '1']),
          2: this.pickCount(d, ['processingCount', 'handlingCount', 'handling', '2']),
          3: this.pickCount(d, ['waitConfirmCount', 'waitConfirm', '3']),
          4: this.pickCount(d, ['finishedCount', 'finished', '4']),
          5: this.pickCount(d, ['archivedCount', 'archived', '5'])
        }
      })
    },
    pickCount(source, keys) {
      for (let i = 0; i < keys.length; i++) {
        const v = source[keys[i]]
        if (v !== undefined && v !== null) return v
      }
      return 0
    },
    getTypeOptions() {
      return customerComplaintTypeApi.findOptions({}).then(data => {
        const d = (data && data.data) || []
        this.typeOptions = Array.isArray(d) ? d : (d.list || [])
      })
    },
    businessFilter(e) {
      this.getBusinessList(e)
    },
    technicianFilter(e) {
      this.getTechnicianList(e)
    },
    getBusinessList(e) {
      return clientListApi.getListPageForMini({
        customerTypes: 2,
        isGetAllParent: true,
        isDeparted: 0,
        pageNo: 1,
        pageSize: 100,
        name: e
      }).then(({ data }) => {
        this.businessList = (data && data.list) || []
      })
    },
    /**
     * 省市区三级树，写法与 client/clientList 保持一致
     */
    getAddressList() {
      return sysAddrApi.getAddressTreeList().then(({ data }) => {
        this.addressList = this.formatCascaderData(data || [])
      })
    },
    formatCascaderData(data) {
      for (let i = 0; i < data.length; i++) {
        // child 为空数组时置为 undefined，否则 el-cascader 会渲染出空的下一级
        if (!data[i].child || data[i].child.length < 1) {
          data[i].child = undefined
        } else {
          this.formatCascaderData(data[i].child)
        }
      }
      return data
    },
    getTechnicianList(e) {
      return technicianInfoApi.fetchList({
        status: 10,
        pageNo: 1,
        pageSize: 100,
        realName: e
      }).then(({ data }) => {
        this.technicianList = (data && data.list) || []
      })
    },
    handleDetail(row) {
      this.$refs.complaintHandle.handleDetail(row)
    },
    handleRemark(row) {
      this.$refs.complaintRemark.handleRemark(row)
    },
    handleArchive(row) {
      return this.$common.confirm('归档后不能再回复或添加备注，是否确认归档？')
        .then(() => customerComplaintApi.archive({ id: row.id }))
        .then(() => {
          this.$message.success('归档成功')
          return this.refresh()
        })
        .catch(() => {})
    },
    handleExport() {
      return customerComplaintApi.exportExcel(this.calcQuery())
    },
    handleBatchExport() {
      if (this.selectItems.length <= 0) {
        this.$common.showMsg('warning', '请先勾选要导出的数据')
        return
      }
      return customerComplaintApi.batchExport(this.selectItems.map(row => row.id))
    }
  }
}
</script>

<style lang="scss" scoped>
.complaint-feedback {
  .stat-row {
    display: flex;
    flex-wrap: wrap;
  }
  .stat-item {
    flex: 1;
    min-width: 160px;
    display: flex;
    align-items: center;
    padding: 12px 16px;
    margin-right: 12px;
    background: #f7f9fc;
    border-radius: 6px;
    cursor: pointer;
    &:last-child {
      margin-right: 0;
    }
  }
  .stat-icon {
    width: 36px;
    height: 36px;
    line-height: 36px;
    text-align: center;
    border-radius: 50%;
    color: #fff;
    margin-right: 12px;
    font-size: 18px;
    &.state-tag-pending { background: #ff9500; }
    &.state-tag-handling { background: #409eff; }
    &.state-tag-confirm { background: #8a5cf5; }
    &.state-tag-finished { background: #22c55e; }
    &.state-tag-archived { background: #909399; }
  }
  .stat-label {
    font-size: 13px;
    color: #8f8e8e;
  }
  .stat-value {
    font-size: 22px;
    font-weight: 600;
    color: #303133;
  }
}
/* 待客户确认=紫色，Element 没有对应 type，单独覆盖 */
::v-deep .el-tag.state-tag-confirm {
  background-color: #f3edff;
  border-color: #ddccff;
  color: #8a5cf5;
}
</style>
